/* eslint-disable prettier/prettier */
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Chip, CircularProgress, Divider, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useState } from 'react'

import ErrorBanner from '../../components/shared/ErrorBanner'
import { PreSendService } from '../../services/sendingDomainService'
import {
    ACCENT,
    BRAND,
    FONT,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
} from '../../theme/tokens'

/**
 * Pre-send report, rendered in Bulk Send's review step.
 *
 * Uday's ask was "all the details once you click on send should come up".
 * Step 3 was a confirmation, not a report -- you pressed send and found out
 * afterwards.
 *
 * The report is built server-side from the same payload /v2/bulk_comms
 * receives, so the recipient count shown is the count that will actually be
 * enqueued rather than a second derivation that can drift from it. Nothing is
 * recomputed here.
 *
 * Blocking is narrow by design and decided by the server: only a paused
 * sending domain, an empty audience, an empty subject and a structurally
 * invalid payload set can_send=false. Over the warmup cap, dropped rows and
 * content warnings all warn. A report that blocks on heuristics gets routed
 * around; one that explains itself gets read.
 */

const LEVEL = {
    pass: { color: STATUS.success.fg, Icon: CheckCircleOutlineIcon },
    warn: { color: STATUS.warning.fg, Icon: WarningAmberIcon },
    fail: { color: STATUS.error.fg, Icon: ErrorOutlineIcon },
}

const Stat = ({ label, value, tone }) => (
    <Box
        sx={{
            flex: '1 1 120px',
            minWidth: 110,
            p: 1.5,
            borderRadius: `${RADIUS.md}px`,
            background: SURFACE.subtle,
            border: `1px solid ${SURFACE.border}`,
            borderTop: `3px solid ${tone || BRAND.orange}`,
        }}
    >
        <Typography
            sx={{
                typography: 'overline',
                color: TEXT.faint,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                typography: 'h4',
                fontWeight: 700,
                color: TEXT.primary,
                mt: 0.25,
            }}
        >
            {value}
        </Typography>
    </Box>
)
Stat.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any,
    tone: PropTypes.string,
}

const Line = ({ level, children }) => {
    const { color, Icon } = LEVEL[level] || LEVEL.warn
    return (
        <Box
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}
        >
            <Icon sx={{ fontSize: ICON.md, color, mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ typography: 'caption', color: TEXT.secondary }}>
                {children}
            </Typography>
        </Box>
    )
}
Line.propTypes = { level: PropTypes.string, children: PropTypes.any }

const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography
            sx={{
                typography: 'overline',
                color: ACCENT.text,
                mb: 1,
            }}
        >
            {title}
        </Typography>
        {children}
    </Box>
)
Section.propTypes = { title: PropTypes.string, children: PropTypes.any }

const PreSendReport = ({
    eventType,
    eventId,
    identities,
    others,
    sender,
    templateBody,
    subject,
    channels,
    onVerdict,
    onRunning,
    timezone,
    eventTime,
    mappedOthers,
    sessionId,
}) => {
    const [report, setReport] = useState(null)
    const [error, setError] = useState('')
    // The report no longer covers the screen while it runs, so the page keeps
    // Send disabled until the verdict is in (onRunning): a click in that
    // window would skip a blocking verdict such as a paused domain.
    const running = (on) => onRunning && onRunning(on)
    const inputs = [eventType, eventId, sender, subject, templateBody]

    // Before the browser paints: Send must not be clickable for even a frame
    // between this report appearing and its request starting.
    useLayoutEffect(() => {
        running(true)
    }, inputs)

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            setError('')
            setReport(null)
            running(true)
            try {
                const res = await PreSendService.report({
                    eventType,
                    eventId,
                    identities,
                    others,
                    sender,
                    templateBody,
                    subject,
                    channels,
                    timezone,
                    eventTime,
                    mappedOthers,
                    sessionId,
                })
                if (cancelled) return
                const data = res?.data?.report || null
                setReport(data)
                // Surface the verdict so the parent can gate its Send button.
                if (onVerdict) onVerdict(data)
            } catch (err) {
                if (cancelled) return
                setError(
                    err?.response?.data?.error ||
                        'Could not build the pre-send report. The send is not blocked by this.'
                )
                // A report failure must not block a legitimate send: the
                // report is advisory, and refusing to send because we could
                // not describe the send would be worse than sending.
                if (onVerdict) onVerdict(null)
            } finally {
                if (!cancelled) running(false)
            }
        }
        run()
        return () => {
            cancelled = true
            running(false)
        }
    }, inputs)

    if (error) {
        return (
            <Box sx={{ mb: 2 }}>
                <ErrorBanner message={error} />
            </Box>
        )
    }

    if (!report) {
        return (
            <Box
                role="status"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.25,
                    py: 3,
                }}
            >
                <CircularProgress size={20} sx={{ color: BRAND.orange }} />
                <Typography sx={{ typography: 'body2', color: TEXT.muted }}>
                    Checking this send…
                </Typography>
            </Box>
        )
    }

    const {
        audience = {},
        warmup = {},
        content = {},
        blockers = [],
        warnings = [],
    } = report

    return (
        <Box>
            {blockers.length > 0 && (
                <Box
                    sx={{
                        p: 2,
                        mb: 2.5,
                        borderRadius: `${RADIUS.md}px`,
                        background: STATUS.error.bg,
                        border: `1px solid ${STATUS.error.border}`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                        }}
                    >
                        <BlockIcon
                            sx={{ fontSize: ICON.md, color: STATUS.error.fg }}
                        />
                        <Typography
                            sx={{
                                typography: 'caption',
                                fontWeight: 700,
                                color: STATUS.error.fg,
                            }}
                        >
                            This send is blocked
                        </Typography>
                    </Box>
                    {blockers.map((b, i) => (
                        <Line key={i} level="fail">
                            {b}
                        </Line>
                    ))}
                </Box>
            )}

            <Section title="Audience">
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        mb: 1.5,
                    }}
                >
                    <Stat
                        label="Recipients"
                        value={(
                            audience.unique_recipients ?? 0
                        ).toLocaleString()}
                    />
                    <Stat
                        label="Rows"
                        value={(audience.total_rows ?? 0).toLocaleString()}
                    />
                    <Stat
                        label="Skipped"
                        value={(audience.dropped_total ?? 0).toLocaleString()}
                        tone={
                            audience.dropped_total
                                ? STATUS.warning.fg
                                : STATUS.success.fg
                        }
                    />
                </Box>
                {audience.contact_column ? (
                    <Typography
                        sx={{ typography: 'caption', color: TEXT.faint }}
                    >
                        deduplicated on {audience.contact_column}
                    </Typography>
                ) : null}
            </Section>

            {warmup.registered && (
                <>
                    <Divider sx={{ mb: 2.5 }} />
                    <Section title="Warmup">
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                mb: 1,
                            }}
                        >
                            <Stat
                                label="Domain day"
                                value={warmup.day ?? '—'}
                            />
                            <Stat
                                label="Today's cap"
                                value={(warmup.cap ?? 0).toLocaleString()}
                            />
                            <Stat
                                label="Remaining"
                                value={(warmup.remaining ?? 0).toLocaleString()}
                                tone={
                                    warmup.would_exceed
                                        ? STATUS.error.fg
                                        : STATUS.success.fg
                                }
                            />
                        </Box>
                        <Chip
                            label={warmup.domain}
                            size="small"
                            sx={{
                                height: 20,
                                typography: 'caption',
                                fontFamily: FONT.mono,
                                color: TEXT.secondary,
                                background: STATUS.neutral.bg,
                            }}
                        />
                    </Section>
                </>
            )}

            {Object.keys(content).length > 0 && (
                <>
                    <Divider sx={{ mb: 2.5 }} />
                    <Section title="Deliverability">
                        {Object.entries(content).map(([channel, summary]) => (
                            <Box key={channel} sx={{ mb: 1.5 }}>
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.muted,
                                        mb: 0.5,
                                    }}
                                >
                                    {channel} — {summary.pass} pass,{' '}
                                    {summary.warn} warn, {summary.fail} fail
                                </Typography>
                                {(summary.checks || [])
                                    // Passing checks are noise in a review
                                    // step; surface only what needs a decision.
                                    .filter((c) => c.level !== 'pass')
                                    .map((c) => (
                                        <Line key={c.id} level={c.level}>
                                            {c.message}
                                        </Line>
                                    ))}
                            </Box>
                        ))}
                    </Section>
                </>
            )}

            {warnings.length > 0 && (
                <>
                    <Divider sx={{ mb: 2.5 }} />
                    <Section title={`Warnings (${warnings.length})`}>
                        {warnings.map((w, i) => (
                            <Line key={i} level="warn">
                                {w}
                            </Line>
                        ))}
                    </Section>
                </>
            )}

            {!blockers.length && !warnings.length && (
                <Line level="pass">
                    No issues found. This send is within the domain&apos;s ramp
                    and the content checks pass.
                </Line>
            )}
        </Box>
    )
}

PreSendReport.propTypes = {
    eventType: PropTypes.string,
    eventId: PropTypes.string,
    identities: PropTypes.object,
    others: PropTypes.object,
    sender: PropTypes.string,
    templateBody: PropTypes.string,
    subject: PropTypes.string,
    channels: PropTypes.array,
    onVerdict: PropTypes.func,
    // Called with true when a report starts and false when it settles (or
    // is dropped), so the page can hold Send until the verdict is in.
    onRunning: PropTypes.func,
    // From the send payload: the scheduled send's zone and moment, the
    // linked variables and the master class session id.
    timezone: PropTypes.string,
    eventTime: PropTypes.string,
    mappedOthers: PropTypes.object,
    sessionId: PropTypes.string,
}

export default PreSendReport
